/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useRef, useState} from 'react';

/**
 * Before/after image comparer: two stacked images with a draggable divider, where
 * the right image is clipped to the area right of the divider.
 *
 * Replaces react-compare-image, which had no version that was both React-17-safe
 * and lean (3.5.x reads a React 19 internal and crashes; 3.4.0 declares
 * jest-environment-jsdom as a runtime dependency). Only these three props were
 * ever used, so the signature matches the library's exactly.
 *
 * Props:
 *   leftImage   — src shown on the left of the divider
 *   rightImage  — src shown on the right of the divider
 *   skeleton    — rendered in place of the images until both have loaded
 */
export const ImageCompareSlider = (props) => {
    const {
        leftImage,
        rightImage,
        skeleton = null,
    } = props;

    const containerRef = useRef(null);

    // A fraction rather than pixels, so a window resize needs no recomputation --
    // both the clip and the handle are expressed as percentages of the container.
    const [dividerFraction, setDividerFraction] = useState(0.5);
    // Which src each side has actually finished loading, rather than a boolean:
    // picking a different comparison year swaps a src on a mounted component, and a
    // flag would stay true, leaving the previous photo on screen until the new one
    // painted. Comparing against the current prop makes the reset fall out of the
    // render, with no effect ordering to lose a race to -- a cached image can fire
    // load before a passive effect runs, which would strand the skeleton forever.
    const [loadedLeftSrc, setLoadedLeftSrc] = useState(null);
    const [loadedRightSrc, setLoadedRightSrc] = useState(null);

    const bothLoaded = loadedLeftSrc === leftImage && loadedRightSrc === rightImage;

    const moveDividerTo = (clientX) => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds?.width) {
            return;
        }
        const fraction = (clientX - bounds.left) / bounds.width;
        setDividerFraction(Math.min(1, Math.max(0, fraction)));
    };

    // Pointer events cover mouse and touch with one set of handlers, and capture
    // keeps the drag tracking once it leaves the element it started on.
    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        moveDividerTo(e.clientX);
    };

    const onPointerMove = (e) => {
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            moveDividerTo(e.clientX);
        }
    };

    const onPointerUp = (e) => {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
    };

    const dividerPercent = `${dividerFraction * 100}%`;

    return (
        <div
            className="sb-image-compare"
            ref={containerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
        >
            {/* Rendered even while hidden: an image only fires load once it is in
                the document, so gating on bothLoaded would deadlock. */}
            <img
                className="sb-image-compare-image"
                src={leftImage}
                alt=""
                style={{opacity: bothLoaded ? 1 : 0}}
                onLoad={() => setLoadedLeftSrc(leftImage)}
            />
            <img
                className="sb-image-compare-image"
                src={rightImage}
                alt=""
                style={{
                    opacity: bothLoaded ? 1 : 0,
                    clipPath: `inset(0 0 0 ${dividerPercent})`,
                }}
                onLoad={() => setLoadedRightSrc(rightImage)}
            />
            {bothLoaded
                ? <div className="sb-image-compare-divider" style={{left: dividerPercent}} aria-hidden="true" />
                : skeleton}
        </div>
    );
};
